import dayjs, { type Dayjs } from "dayjs"
import pluginAdvancedFormat from "dayjs/plugin/advancedFormat"
import pluginIsBetween from "dayjs/plugin/isBetween"
import pluginIsYesterday from "dayjs/plugin/isYesterday"
import pluginMinMax from "dayjs/plugin/minMax"
import pluginRelativeTime from "dayjs/plugin/relativeTime"
import pluginTimezone from "dayjs/plugin/timezone"
import pluginUtc from "dayjs/plugin/utc"

dayjs.extend(pluginUtc)
dayjs.extend(pluginRelativeTime)
dayjs.extend(pluginMinMax)
dayjs.extend(pluginTimezone)
dayjs.extend(pluginAdvancedFormat)
dayjs.extend(pluginIsYesterday)
dayjs.extend(pluginIsBetween)

export { dayjs, type Dayjs }

export const TIME_FORMAT = "HH:mm"

export const DATE_FORMAT = "MMMM D, YYYY"

export const DATE_TIME_FORMAT = "MMM D, YYYY h:mm a"

export const today = () => dayjs()

export const getMonthAbbreviation = (date: Date = new Date()) => {
	return date.toLocaleString(undefined, { month: "long" })
}

export const asDate = (date?: string | number | null | Date) => dayjs(date)

export const formatDuration = (duration: number) => {
	const minutes = Math.floor(duration / 60)
	const seconds = duration % 60
	return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
}

export const formatAsTime = (dateString: string) =>
	asDate(dateString).format(TIME_FORMAT)

export const formatDateByDay = (day: string) => {
	const currentDate = asDate()
	const formattedDate = currentDate.date(parseInt(day, 10))
	return formattedDate.format(DATE_FORMAT)
}

export const formatDate = (dateString: string) => {
	return asDate(dateString).format(DATE_FORMAT)
}

export const isToday = (date: string | Dayjs) => {
	const dayjsDate = typeof date === "string" ? asDate(date) : date
	return dayjsDate.isSame(asDate(), "day")
}

export const isYesterday = (date: string | Dayjs) => {
	const dayjsDate = typeof date === "string" ? asDate(date) : date
	return dayjsDate.isYesterday()
}

export const getTimeZone = (): string => {
	const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
	const formattedDate = new Date().toLocaleDateString(undefined, {
		timeZoneName: "short",
		timeZone,
	})
	return formattedDate.match(/\bGMT[+-]\d{1,2}\b|\b[A-Z]{1,4}\b/)?.[0] || ""
}

type DateFormatterOptions = {
	hasYear?: boolean
	onlyWeekday?: boolean
	onlyYear?: boolean
	needSimplify?: boolean
}

export const dateFormatter = (
	date?: string | null,
	options?: DateFormatterOptions,
) => {
	if (!date) return "--"
	const {
		hasYear = true,
		onlyWeekday = false,
		needSimplify = false,
	} = options || {}
	const originalDate = new Date(date)
	const dayjsDate = asDate(date)
	const intlOptions = {
		month: "long",
		day: "numeric",
	} as Intl.DateTimeFormatOptions
	const defaultDateOptions: Intl.DateTimeFormatOptions = hasYear
		? {
				year: "numeric",
				...intlOptions,
			}
		: intlOptions
	const timeZoneStr = `, ${getTimeZone()}`
	const defaultDateStr = `${originalDate.toLocaleDateString(undefined, defaultDateOptions)}, ${dayjsDate.format(TIME_FORMAT)}${timeZoneStr}`

	if (!needSimplify) {
		return defaultDateStr
	}
	if (isToday(date)) {
		return "Today"
	}
	if (isYesterday(date)) {
		return "Yesterday"
	}
	if (onlyWeekday) {
		return dayjsDate.format("dddd")
	}

	return defaultDateStr
}

export const dateAsDay = (date: string) => {
	const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
	const localLanguage = navigator.language
	return new Intl.DateTimeFormat(localLanguage, {
		weekday: "short",
		year: "numeric",
		month: "short",
		day: "numeric",
		timeZone,
	}).format(new Date(date))
}

export const dateAsHour = (date: string) => {
	return new Intl.DateTimeFormat("en-US", {
		hour: "2-digit",
		hour12: true,
	}).format(new Date(date))
}

export const dateAsDay2Digit = (date: string) => {
	return new Intl.DateTimeFormat("en-US", {
		day: "2-digit",
	}).format(new Date(date))
}

export const formatTimeDiff = (from: string, to: string) => {
	const diffInMinutes = Math.abs(asDate(to).diff(from, "minute", true))

	if (diffInMinutes < 60) {
		return `${Math.round(diffInMinutes)} mins`
	}

	const diffInHours = diffInMinutes / 60
	const rounded = Math.round(diffInHours * 10) / 10 // one decimal place

	if (rounded === 1) {
		return `1 hr`
	}

	return `${rounded} hrs`
}

export const buildTimeOptions = (timestamp: string, since?: boolean) => {
	const times = []
	const interval = 15
	const totalMinutesInDay = 24 * 60 - 15
	const date = asDate(timestamp)
	const initialDate = since ? date : date.startOf("day")

	for (
		let minutes = since ? 15 : 0;
		minutes < totalMinutesInDay;
		minutes += interval
	) {
		const value = initialDate.add(minutes, "minute")
		const label = `${value.format("h:mma")}${since ? ` (${formatTimeDiff(date.add(minutes, "minute").toISOString(), timestamp)})` : ""}`
		if (value.isSame(initialDate, "day")) {
			times.push({ label, value: value.toISOString() })
		}
	}

	return times
}

export const getChatMessageDisplayTime = (timestamp: Dayjs, prefix = true) => {
	const now = asDate()
	let timeString = timestamp.format("HH:mm A")

	if (isToday(timestamp)) {
		timeString = `${prefix ? `Today ` : ""}${timeString}`
	} else if (isYesterday(timestamp)) {
		return `${prefix ? `Yesterday ` : ""}${timeString}`
	} else if (timestamp.isSame(now, "week")) {
		timeString = timestamp.format("ddd HH:mm A")
	} else if (timestamp.year() === now.year()) {
		timeString = timestamp.format("MMM D HH:mm A")
	} else {
		timeString = timestamp.format("ddd YYYY MMM D")
	}
	return timeString
}
